import React from "react";
import { useNode } from "@craftjs/core";
import "../../styles/components/user/UserComponents.css";
import "../../styles/landing.css";

import { Container } from "./Container";
import { Text } from "./Text";
import { Image } from "./Image";

interface FeatureItemProps {
  imageSrc: string;
  title: string;
  description: string;
}

const FeatureItem = ({ imageSrc, title, description }: FeatureItemProps) => {
  return (
    <Container
      background="#f9f9f9"
      padding="20px"
      borderRadius={4}
      margin="0 0 20px 0"
      flexDirection="row"
      alignItems="flex-start"
      justifyContent="flex-start"
      gap="20px"
      width="100%"
    >
      <Image
        src={imageSrc}
        alt={title}
        width="88px"
        height="88px"
        borderRadius={4}
      />
      <Container
        background="transparent"
        padding="0"
        margin="0"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="flex-start"
        gap="8px"
      >
        <Text
          text={title}
          fontSize={20}
          fontWeight="bold"
          color="#333"
          margin="0"
        />
        <Text text={description} fontSize={16} color="#555" margin="0" />
      </Container>
    </Container>
  );
};

interface FeatureSectionProps {
  icon?: string;
  title?: string;
  description?: string;
  background?: string;
  width?: string;
  margin?: string;
}

export const FeatureSection = ({
  icon,
  title,
  description,
  background = "#ffffff",
  width = "100%",
  margin = "0",
}: FeatureSectionProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  // If props are provided, use them instead of default features
  const useCustomProps = icon && title && description;

  const features = [
    {
      imageSrc:
        "https://thegamingclub-lt.com/os-ui/static/lt-thegamingclub-msisdn-cmp-nid/files/225d145a40edb0c24d50..jpg",
      title: "Fast loading games",
      description:
        "Play the latest games easily on your computer, tablet or phone. Thousands of choices from the best games from around the world.",
    },
    {
      imageSrc:
        "https://thegamingclub-lt.com/os-ui/static/lt-thegamingclub-msisdn-cmp-nid/files/23b1b5dc0d5a8a468c50..jpg",
      title: "No commitment required",
      description:
        "Cancel your subscription anytime, anywhere. or contact our support team to help you with the service.",
    },
    {
      imageSrc:
        "https://thegamingclub-lt.com/os-ui/static/lt-thegamingclub-msisdn-cmp-nid/files/033ff34d4048034a869c..jpg",
      title: "Add to favorites list",
      description:
        "Add and customize what you want to play from our library, follow and follow your favorite game.",
    },
  ];

  // Use a div with a ref as a wrapper instead of trying to pass ref directly to Container
  return (
    <div
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      className={selected ? "selected-component" : ""}
      style={{ width, margin }}
    >
      {useCustomProps ? (
        <Container
          background={background}
          padding="30px 20px"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          gap="15px"
          borderRadius={8}
          boxShadow="0 2px 5px rgba(0,0,0,0.05)"
        >
          <Text
            text={icon || "🎮"}
            fontSize={48}
            fontWeight="normal"
            color="#333"
            margin="0 0 10px 0"
          />
          <Text
            text={title || "Feature Title"}
            fontSize={20}
            fontWeight="bold"
            color="#333"
            margin="0 0 10px 0"
            textAlign="center"
          />
          <Text
            text={description || "Feature description goes here"}
            fontSize={16}
            color="#555"
            margin="0"
            textAlign="center"
          />
        </Container>
      ) : (
        <Container
          background={background}
          padding="40px 20px"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          width="100%"
          gap="30px"
        >
          <Text
            text="Why choose us?"
            fontSize={28}
            fontWeight="bold"
            color="#333"
            margin="0 0 10px 0"
          />

          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              imageSrc={feature.imageSrc}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </Container>
      )}
    </div>
  );
};

export const FeatureSectionSettings = () => {
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props || {},
  }));

  return (
    <div className="settings-container">
      <h4>Feature Section Settings</h4>
      <div className="form-group">
        <label>Icon</label>
        <input
          type="text"
          className="form-control"
          value={props.icon || ""}
          onChange={(e) =>
            setProp((props: FeatureSectionProps) => ({
              ...props,
              icon: e.target.value,
            }))
          }
        />
      </div>
      <div className="form-group">
        <label>Title</label>
        <input
          type="text"
          className="form-control"
          value={props.title || ""}
          onChange={(e) =>
            setProp((props: FeatureSectionProps) => ({
              ...props,
              title: e.target.value,
            }))
          }
        />
      </div>
      <div className="form-group">
        <label>Description</label>
        <textarea
          className="form-control"
          value={props.description || ""}
          onChange={(e) =>
            setProp((props: FeatureSectionProps) => ({
              ...props,
              description: e.target.value,
            }))
          }
        />
      </div>
      <div className="form-group">
        <label>Background Color</label>
        <input
          type="color"
          className="form-control"
          value={props.background || "#ffffff"}
          onChange={(e) =>
            setProp((props: FeatureSectionProps) => ({
              ...props,
              background: e.target.value,
            }))
          }
        />
      </div>
      <div className="form-group">
        <label>Width</label>
        <input
          type="text"
          className="form-control"
          value={props.width || "100%"}
          onChange={(e) =>
            setProp((props: FeatureSectionProps) => ({
              ...props,
              width: e.target.value,
            }))
          }
        />
      </div>
      <div className="form-group">
        <label>Margin</label>
        <input
          type="text"
          className="form-control"
          value={props.margin || "0"}
          onChange={(e) =>
            setProp((props: FeatureSectionProps) => ({
              ...props,
              margin: e.target.value,
            }))
          }
        />
      </div>
    </div>
  );
};

FeatureSection.craft = {
  displayName: "Feature Section",
  props: {
    icon: "",
    title: "",
    description: "",
    background: "#ffffff",
    width: "100%",
    margin: "0",
  },
  related: {
    settings: FeatureSectionSettings,
  },
};
